import { useEffect, useState, memo } from 'react';
import { Button } from '@/components/ui/button';
import ArrowLeftIcon from '@/components/icons/leftArrow';
import IconArchireportAndText from '@/components/icons/iconArchireportAndText';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/authContext';
import { useCartContext } from '@/contexts/cartContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import i18n from '@/utils/i18n';
import { getCurrencySymbol, getCurrencyName } from '@/utils/prices';
import { useSearchParams } from 'react-router-dom';
import ThemeToggle from '@/components/commons/themeToggle';

export default memo(function Header() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { region } = useAuthContext();
  const forcedCurrency = searchParams.get('currency');
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>([]);
  const { prices, currency: cartCurrency, setCartPartialState } = useCartContext();

  useEffect(() => {
    // keeps only common currencies between prices
    if (prices.length) {
      const commonCurrencies = prices
        .map(price => Object.keys(price.currency_options))
        .reduce((commonCurrencies, currentArray) => {
          return commonCurrencies.filter(currency => currentArray.includes(currency));
        });

      setAvailableCurrencies(commonCurrencies);
    }
  }, [prices]);

  useEffect(() => {
    if (forcedCurrency && forcedCurrency !== cartCurrency) {
      setCartPartialState({ currency: forcedCurrency });
    }
  }, [cartCurrency, forcedCurrency, setCartPartialState]);

  const backAction = () => {
    if (navigator.userAgent.toLowerCase().indexOf(' electron/') > -1) {
      window.opener.postMessage('start-sync', '*');
      window.close();
    } else {
      window.location.href = `${region?.web || 'https://web.archireport.com'}/web/subscription`;
    }
  };

  const changeCurrency = (currency: string) => {
    setCartPartialState({ currency });

    searchParams.set('currency', currency);
    setSearchParams(searchParams);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between h-16 px-2 lg:px-8 border-b border-border bg-background">
      <Button variant="ghost" size="xs" className="mr-4" onClick={backAction}>
        <ArrowLeftIcon className="h-5 w-5" />
        <span className="sr-only">{t('Global_Back')}</span>
      </Button>
      <div className="flex items-center gap-2">
        <IconArchireportAndText className="h-8 w-auto" />
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 min-w-[200px]">
          {availableCurrencies?.length > 0 && (
            <Select value={forcedCurrency || cartCurrency || 'eur'} onValueChange={changeCurrency}>
              <SelectTrigger id="currency" className="w-full">
                <SelectValue placeholder="" />
              </SelectTrigger>
              <SelectContent>
                {availableCurrencies.map(currency => (
                  <SelectItem key={currency} value={currency}>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-sm w-8 inline-block text-center font-mono">
                          {getCurrencySymbol(i18n.language, currency)}
                        </span>
                        <div className="flex flex-col">
                          <span className="font-medium">{getCurrencyName(i18n.language, currency)}</span>
                          <span className="text-xs text-muted-foreground">{currency.toUpperCase()}</span>
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <ThemeToggle className="ml-6" />
      </div>
    </header>
  );
});
